import {SidebarInset, SidebarProvider} from '@hmlet/ui'

import {Outlet, createFileRoute, redirect, useNavigate} from '@tanstack/react-router'
import {useEffect} from 'react'

import AppHeader from '@components/AppHeader'
import {AppSidebar} from '@components/AppNavBar'
import {useUser} from '@contexts/UserContext'

import {isIsoDate, normalizeRange} from '@utils/dates'

import type {Compare, PeriodSearch, Preset, Region} from '@app-types/api'

const PRESETS: Preset[] = ['mtd', 'yesterday', 'last_7', 'last_30', 'last_90', 'last_month', 'ytd', 'custom']
const COMPARES: Compare[] = ['none', 'prev', 'wow', 'yoy']
const REGION_CHOICES: Region[] = ['all', 'sg', 'hk']  // jp is the default and is not kept in the URL

export const Route = createFileRoute('/_protected')({
  // Period and comparison are URL search params shared by every panel.
  validateSearch: (search: Record<string, unknown>): PeriodSearch => {
    const preset = PRESETS.includes(search.preset as Preset) ? (search.preset as Preset) : undefined
    const compare = COMPARES.includes(search.compare as Compare) ? (search.compare as Compare) : undefined
    const region = REGION_CHOICES.includes(search.region as Region) ? (search.region as Region) : undefined
    // A custom range is always complete, in order and not after yesterday, so the page never shows another period than the URL says.
    if (preset === 'custom' || (isIsoDate(search.from) && isIsoDate(search.to))) {
      return {preset: 'custom', compare, region, ...normalizeRange(search.from, search.to)}
    }
    return {preset, compare, region}
  },
  // Runs on route entry only, not reactive: the effect below covers logout while on the page.
  beforeLoad: ({context}) => {
    if (!context.auth.isAuthenticated) throw redirect({to: '/login'})
  },
  component: ProtectedLayout,
})

function ProtectedLayout() {
  const navigate = useNavigate()
  const {isAuthenticated, isUserDetailsLoading} = useUser()

  useEffect(() => {
    if (!isUserDetailsLoading && !isAuthenticated) navigate({to: '/login', replace: true})
  }, [isAuthenticated, isUserDetailsLoading, navigate])

  if (!isAuthenticated) return null

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset className="min-w-0">
        <AppHeader />
        <div className="flex w-full min-w-0 flex-col gap-4 p-4 md:p-6">
          <Outlet />
        </div>
      </SidebarInset>
    </SidebarProvider>
  )
}
